import React, { useEffect, useState } from "react";
import LinksComponent from "../../../components/Tenant/LinksComponent/LinksComponent";
import DataTableComponent from "../../../components/Tenant/DataTableComponent/DataTableComponent";
import ModalComponent from "../../../components/Tenant/ModalComponent/ModalComponent";
import { toast } from "react-hot-toast";
import {
  useGetPendingApprovalQueueMutation,
  useGetAllApprovalRequestsMutation,
  useGetApprovalRequestHistoryMutation,
  useApproveApprovalRequestMutation,
  useRejectApprovalRequestMutation,
} from "../../../features/tenant/approvalLog/approvalLog.Api";

const STATUS_BADGE = {
  pending:  "bg-yellow-100 text-yellow-700",
  approved: "bg-green-100 text-green-700",
  rejected: "bg-red-100 text-red-600",
};

const ACTION_ICON = {
  pending:  "⏳",
  approved: "✅",
  rejected: "❌",
};

const ApprovalRequests = () => {
  const primaryColor = JSON.parse(sessionStorage.getItem("vendorTheme"))?.theme?.color_shades;
  const token        = sessionStorage.getItem("access_token");
  const vendorData   = JSON.parse(sessionStorage.getItem("vendorData"));
  const tenant_id    = vendorData?.tenant_id;

  // ── Tabs ─────────────────────────────────────────────────────────────────────
  const [activeTab, setActiveTab] = useState("queue");  // "queue" | "all"

  // ── Pagination ────────────────────────────────────────────────────────────────
  const [limit, setLimit]     = useState(10);
  const [offset, setOffset]   = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [paginationResetToggle, setPaginationResetToggle] = useState(false);

  // ── Filters (All Requests tab) ────────────────────────────────────────────────
  const [filterStatus, setFilterStatus]         = useState("");
  const [filterEntityType, setFilterEntityType] = useState("");

  // ── Data ──────────────────────────────────────────────────────────────────────
  const [queueData, setQueueData]   = useState([]);
  const [queueTotal, setQueueTotal] = useState(0);
  const [allData, setAllData]       = useState([]);
  const [allTotal, setAllTotal]     = useState(0);

  // ── History modal ─────────────────────────────────────────────────────────────
  const [historyModal, setHistoryModal] = useState(false);
  const [historyData, setHistoryData]   = useState([]);
  const [historyTitle, setHistoryTitle] = useState("");

  // ── Approve modal ─────────────────────────────────────────────────────────────
  const [approveModal, setApproveModal]   = useState(false);
  const [approveTarget, setApproveTarget] = useState(null);
  const [approveRemarks, setApproveRemarks] = useState("");

  // ── Reject modal ──────────────────────────────────────────────────────────────
  const [rejectModal, setRejectModal]   = useState(false);
  const [rejectTarget, setRejectTarget] = useState(null);
  const [rejectRemarks, setRejectRemarks] = useState("");

  // ── API hooks ──────────────────────────────────────────────────────────────────
  const [getPendingApprovalQueue, { isLoading: queueLoading }] = useGetPendingApprovalQueueMutation();
  const [getAllApprovalRequests,  { isLoading: allLoading }]   = useGetAllApprovalRequestsMutation();
  const [getApprovalRequestHistory]  = useGetApprovalRequestHistoryMutation();
  const [approveApprovalRequest]     = useApproveApprovalRequestMutation();
  const [rejectApprovalRequest]      = useRejectApprovalRequestMutation();

  // ── Fetch on tab / pagination change ─────────────────────────────────────────
  useEffect(() => {
    if (activeTab === "queue") fetchQueue();
    else fetchAll();
  }, [activeTab, limit, offset]);

  const fetchQueue = async () => {
    try {
      const res = await getPendingApprovalQueue({ token, tenant_id, limit, offset }).unwrap();
      if (res?.status) {
        setQueueData(res.body?.data || []);
        setQueueTotal(res.body?.total || 0);
      }
    } catch { toast.error("Failed to load pending queue"); }
  };

  const fetchAll = async () => {
    try {
      const res = await getAllApprovalRequests({
        token, tenant_id, limit, offset,
        status: filterStatus || undefined,
        entity_type: filterEntityType || undefined,
      }).unwrap();
      if (res?.status) {
        setAllData(res.body?.data || []);
        setAllTotal(res.body?.total || 0);
      }
    } catch { toast.error("Failed to load requests"); }
  };

  // ── Pagination handlers ────────────────────────────────────────────────────────
  const handlePageChange = (page) => {
    setOffset((page - 1) * limit);
    setCurrentPage(page);
  };
  const handlePerRowsChange = (newPerPage) => {
    setLimit(newPerPage);
    setOffset(0);
    setCurrentPage(1);
    setPaginationResetToggle((prev) => !prev);
  };

  // ── View history ───────────────────────────────────────────────────────────────
  const handleViewHistory = async (row) => {
    try {
      const res = await getApprovalRequestHistory({
        token, tenant_id,
        workflow_id: row.workflow_id,
        ref_table_id: row.ref_table_id,
      }).unwrap();
      if (res?.status) {
        setHistoryData(res.body || []);
        setHistoryTitle(`History — ${row.entity_type.replace(/_/g, " ")} #${row.ref_table_id}`);
        setHistoryModal(true);
      }
    } catch { toast.error("Failed to load history"); }
  };

  // ── Approve ───────────────────────────────────────────────────────────────────
  const openApprove = (row) => {
    setApproveTarget(row);
    setApproveRemarks("");
    setApproveModal(true);
  };

  const confirmApprove = async () => {
    try {
      const res = await approveApprovalRequest({
        token, tenant_id,
        data: {
          workflow_id:  approveTarget.workflow_id,
          ref_table_id: approveTarget.ref_table_id,
          remarks:      approveRemarks || null,
        },
      }).unwrap();
      if (res?.status) {
        toast.success(res.message || "Approved");
        setApproveModal(false);
        fetchQueue();
      } else {
        toast.error(res?.message || "Failed to approve");
      }
    } catch (err) {
      toast.error(err?.data?.message || "Something went wrong");
    }
  };

  // ── Reject ────────────────────────────────────────────────────────────────────
  const openReject = (row) => {
    setRejectTarget(row);
    setRejectRemarks("");
    setRejectModal(true);
  };

  const confirmReject = async () => {
    if (!rejectRemarks.trim()) return toast.error("Remarks are required when rejecting");
    try {
      const res = await rejectApprovalRequest({
        token, tenant_id,
        data: {
          workflow_id:  rejectTarget.workflow_id,
          ref_table_id: rejectTarget.ref_table_id,
          remarks:      rejectRemarks.trim(),
        },
      }).unwrap();
      if (res?.status) {
        toast.success("Rejected");
        setRejectModal(false);
        fetchQueue();
      } else {
        toast.error(res?.message || "Failed to reject");
      }
    } catch (err) {
      toast.error(err?.data?.message || "Something went wrong");
    }
  };

  // ── My Queue columns ──────────────────────────────────────────────────────────
  const queueColumns = [
    {
      name: "S.No",
      cell: (row, idx) => offset + idx + 1,
      width: "70px",
    },
    {
      name: "Entity Type",
      cell: (row) => <span className="capitalize font-medium">{row.entity_type?.replace(/_/g, " ")}</span>,
      sortable: true,
    },
    { name: "Ref ID",       selector: (row) => row.ref_table_id, sortable: true, width: "90px" },
    { name: "Table",        selector: (row) => row.ref_table_name, sortable: true },
    {
      name: "Level",
      cell: (row) => (
        <span className="text-xs bg-blue-50 text-blue-700 px-2 py-0.5 rounded-full">
          L{row.level} / {row.total_levels}
        </span>
      ),
      width: "90px",
    },
    {
      name: "Submitted By",
      cell: (row) => <span className="text-sm">{row.user_name}</span>,
    },
    {
      name: "Date",
      cell: (row) => new Date(row.created_at).toLocaleString("en-IN"),
      sortable: true,
      width: "170px",
    },
    {
      name: "Actions",
      cell: (row) => (
        <div className="flex gap-2">
          <button
            onClick={() => openApprove(row)}
            className="flex justify-center items-center rounded bg-success px-3 pb-1.5 pt-1.5 text-xs font-medium uppercase leading-normal text-white shadow-[0_4px_9px_-4px_#14a44d] transition duration-150 ease-in-out hover:bg-success-600"
          >
            Approve
          </button>
          <button
            onClick={() => openReject(row)}
            className="flex justify-center items-center rounded bg-danger px-3 pb-1.5 pt-1.5 text-xs font-medium uppercase leading-normal text-white shadow-lg transition duration-150 ease-in-out hover:bg-danger-600"
          >
            Reject
          </button>
        </div>
      ),
      minWidth: "180px",
    },
  ];

  // ── All Requests columns ──────────────────────────────────────────────────────
  const allColumns = [
    {
      name: "S.No",
      cell: (row, idx) => offset + idx + 1,
      width: "70px",
    },
    {
      name: "Entity Type",
      cell: (row) => <span className="capitalize font-medium">{row.entity_type?.replace(/_/g, " ")}</span>,
      sortable: true,
    },
    { name: "Ref ID", selector: (row) => row.ref_table_id, sortable: true, width: "90px" },
    {
      name: "Status",
      cell: (row) => (
        <span className={`px-2 py-0.5 rounded-full text-xs font-semibold ${STATUS_BADGE[row.status] || ""}`}>
          {row.status}
        </span>
      ),
      width: "100px",
    },
    {
      name: "Level",
      cell: (row) => (
        <span className="text-xs bg-gray-100 px-2 py-0.5 rounded-full">
          L{row.level} / {row.total_levels}
        </span>
      ),
      width: "90px",
    },
    { name: "Actor", cell: (row) => row.user_name },
    {
      name: "Date",
      cell: (row) => new Date(row.created_at).toLocaleString("en-IN"),
      sortable: true,
      width: "170px",
    },
    {
      name: "History",
      cell: (row) => (
        <button
          onClick={() => handleViewHistory(row)}
          className="btn btn-outline-primary btn-sm"
        >
          View
        </button>
      ),
      width: "90px",
    },
  ];

  return (
    <>
      <div className="flex w-full bg-gray-200 h-full justify-center">
        <div className="w-full">
          {/* ── Page Header ───────────────────────────────────────────────────── */}
          <div className="border-b bg-white border-gray-300 pl-6 py-2 shadow-sm text-xl font-bold">
            Approval Requests
            <LinksComponent />
          </div>

          {/* ── Filter / Tab Bar ──────────────────────────────────────────────── */}
          <div className="border-b border-gray-300 bg-gray-50 rounded-t-lg p-3">
            {/* Tabs */}
            <div className="flex space-x-2 mb-3">
              {[
                { key: "queue", label: "My Queue" },
                { key: "all",   label: "All Requests" },
              ].map((tab) => (
                <button
                  key={tab.key}
                  onClick={() => { setActiveTab(tab.key); setOffset(0); setCurrentPage(1);
    setPaginationResetToggle((prev) => !prev); }}
                  className={`btn ${activeTab === tab.key ? "btn-outline-primary" : "btn-outline-secondary"}`}
                >
                  {tab.label}
                </button>
              ))}
            </div>

            {/* Filters for All Requests */}
            {activeTab === "all" && (
              <div className="flex w-full space-x-2">
                <div className="relative w-full searchin">
                  <label className="block text-sm font-medium capitalize leading-6 text-gray-900 mb-1">
                    Status
                  </label>
                  <select
                    className="w-full p-2 text-sm text-gray-900 border border-gray-300 rounded-lg bg-white focus:ring-blue-500 focus:border-blue-500 inputp"
                    value={filterStatus}
                    onChange={(e) => setFilterStatus(e.target.value)}
                  >
                    <option value="">All Statuses</option>
                    <option value="pending">Pending</option>
                    <option value="approved">Approved</option>
                    <option value="rejected">Rejected</option>
                  </select>
                </div>
                <div className="relative w-full searchin">
                  <label className="block text-sm font-medium capitalize leading-6 text-gray-900 mb-1">
                    Entity Type
                  </label>
                  <input
                    type="search"
                    className="w-full p-2 text-sm text-gray-900 border border-gray-300 rounded-lg bg-white focus:ring-blue-500 focus:border-blue-500 inputp"
                    placeholder="Filter by entity type..."
                    value={filterEntityType}
                    onChange={(e) => setFilterEntityType(e.target.value)}
                  />
                </div>
                <div className="">
                  <label className="block text-sm font-medium capitalize leading-6 text-gray-900 mb-1">
                    &nbsp;
                  </label>
                  <div className="flex flex-row gap-2">
                    <button
                      onClick={fetchAll}
                      className="btn btn-outline-primary pdbtn_all"
                    >
                      Search
                    </button>
                    <button
                      onClick={() => { setFilterStatus(""); setFilterEntityType(""); }}
                      className="btn btn-outline-primary pdbtn_all whitespace-nowrap"
                    >
                      Clear Filter
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* ── Table ─────────────────────────────────────────────────────────── */}
          <div className="p-3">
            <div className="p-3 w-full bg-white shadow rounded-lg">
              <DataTableComponent
                columns={activeTab === "queue" ? queueColumns : allColumns}
                data={activeTab === "queue" ? queueData : allData}
                isLoading={activeTab === "queue" ? queueLoading : allLoading}
                totalRows={activeTab === "queue" ? queueTotal : allTotal}
                handlePageChange={handlePageChange}
                handlePerRowsChange={handlePerRowsChange}
                paginationDefaultPage={currentPage}
                paginationResetDefaultPage={paginationResetToggle}
              />
            </div>
          </div>
        </div>
      </div>

      {/* ── History Modal ──────────────────────────────────────────────────────── */}
      <ModalComponent size="lg" show={historyModal} setShow={setHistoryModal} title={historyTitle}>
        <div className="flex flex-col gap-2 p-4">
          {historyData.length === 0 ? (
            <p className="text-sm text-gray-400">No history found.</p>
          ) : (
            <div className="relative border-l-2 border-gray-200 ml-4">
              {historyData.map((h, idx) => (
                <div key={h.id} className="mb-5 ml-6 relative">
                  {/* Timeline dot */}
                  <span
                    className={`absolute -left-[30px] flex items-center justify-center w-6 h-6 rounded-full text-sm ring-4 ring-white ${
                      h.status === "approved" ? "bg-green-100" :
                      h.status === "rejected" ? "bg-red-100" : "bg-yellow-100"
                    }`}
                  >
                    {ACTION_ICON[h.status]}
                  </span>

                  <div className="bg-gray-50 border rounded p-3">
                    <div className="flex justify-between items-start">
                      <div>
                        <p className="text-sm font-semibold text-gray-700">
                          Level {h.level} —{" "}
                          <span className={`capitalize px-1.5 py-0.5 rounded text-xs ${STATUS_BADGE[h.status]}`}>
                            {h.status}
                          </span>
                        </p>
                        <p className="text-xs text-gray-500 mt-0.5">
                          By <strong>{h.user_name}</strong> ({h.user_role_name})
                        </p>
                        {h.remarks && (
                          <p className="text-xs text-gray-600 mt-1 italic">
                            "{h.remarks}"
                          </p>
                        )}
                      </div>
                      <p className="text-xs text-gray-400 whitespace-nowrap ml-4">
                        {new Date(h.created_at).toLocaleString("en-IN")}
                      </p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </ModalComponent>

      {/* ── Approve Modal ──────────────────────────────────────────────────────── */}
      <ModalComponent size="sm" show={approveModal} setShow={setApproveModal} title="Approve Request">
        <div className="flex flex-col gap-4 p-4">
          <p className="text-sm text-gray-600">
            Approving <strong>{approveTarget?.entity_type?.replace(/_/g, " ")}</strong> #{approveTarget?.ref_table_id} at Level {approveTarget?.level}.
          </p>
          <div>
            <label className="block text-sm font-medium capitalize leading-6 text-gray-900 mb-1">
              Remarks (optional)
            </label>
            <textarea
              className="w-full border rounded px-3 py-2 text-sm mt-1"
              rows={3}
              placeholder="Add a note..."
              value={approveRemarks}
              onChange={(e) => setApproveRemarks(e.target.value)}
            />
          </div>
          <div className="flex justify-end gap-2">
            <button onClick={() => setApproveModal(false)} className="px-4 py-2 border rounded text-sm text-gray-600">
              Cancel
            </button>
            <button
              onClick={confirmApprove}
              className="flex justify-center items-center rounded bg-success px-4 py-2 text-sm font-medium text-white shadow-[0_4px_9px_-4px_#14a44d] transition duration-150 ease-in-out hover:bg-success-600"
            >
              Confirm Approve
            </button>
          </div>
        </div>
      </ModalComponent>

      {/* ── Reject Modal ───────────────────────────────────────────────────────── */}
      <ModalComponent size="sm" show={rejectModal} setShow={setRejectModal} title="Reject Request">
        <div className="flex flex-col gap-4 p-4">
          <p className="text-sm text-gray-600">
            Rejecting <strong>{rejectTarget?.entity_type?.replace(/_/g, " ")}</strong> #{rejectTarget?.ref_table_id} at Level {rejectTarget?.level}.
          </p>
          <div>
            <label className="block text-sm font-medium capitalize leading-6 text-gray-900 mb-1">
              Remarks * <span className="text-red-500">(required)</span>
            </label>
            <textarea
              className="w-full border rounded px-3 py-2 text-sm mt-1"
              rows={3}
              placeholder="Reason for rejection..."
              value={rejectRemarks}
              onChange={(e) => setRejectRemarks(e.target.value)}
            />
          </div>
          <div className="flex justify-end gap-2">
            <button onClick={() => setRejectModal(false)} className="px-4 py-2 border rounded text-sm text-gray-600">
              Cancel
            </button>
            <button
              onClick={confirmReject}
              className="flex justify-center items-center rounded bg-danger px-4 py-2 text-sm font-medium text-white shadow-lg transition duration-150 ease-in-out hover:bg-danger-600"
            >
              Confirm Reject
            </button>
          </div>
        </div>
      </ModalComponent>
    </>
  );
};

export default ApprovalRequests;
